<template>
  <div class="news-detail-page">
    <!-- 面包屑导航 -->
    <div class="breadcrumb">
      <div class="container">
        <span class="link" @click="goHome">首页</span>
        <span class="separator">/</span>
        <span class="link" @click="goToNewsList">{{ newsCategory }}</span>
        <span class="separator">/</span>
        <span class="current">详情</span>
      </div>
    </div>

    <!-- 详情内容 -->
    <div class="detail-content">
      <div class="container">
        <!-- 加载中 -->
        <div v-if="loading" class="loading-state">
          <p>加载中...</p>
        </div>

        <!-- 错误提示 -->
        <div v-else-if="error" class="error-state">
          <p>{{ error }}</p>
          <button @click="fetchNewsDetail">重新加载</button>
        </div>

        <!-- 内容卡片 -->
        <div v-else class="content-card">
          <h1 class="news-title">{{ newsDetail.title }}</h1>
          <div class="news-meta">
            <span class="date-icon">
              <svg data-v-8519a06a="" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect data-v-8519a06a="" x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line data-v-8519a06a="" x1="16" y1="2" x2="16" y2="6"></line>
                <line data-v-8519a06a="" x1="8" y1="2" x2="8" y2="6"></line>
                <line data-v-8519a06a="" x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
            </span>

            <span class="news-date">{{ newsDetail.publishTime || newsDetail.createTime }}</span>
          </div>

          <!-- 正文内容 -->
          <div class="news-body" v-if="newsDetail.content">
            <div class="content-html" v-html="newsDetail.content"></div>
          </div>

          <!-- 附件列表 -->
          <div v-if="attachments.length > 0" class="attachments-section">
            <h3 class="section-title">📎 相关附件</h3>
            <div class="attachments-list">
              <a v-for="file in attachments" :key="file.id" :href="resolveAttachmentUrl(file.attachmentPath)" rel="noopener noreferrer"
                class="attachment-item" @click="onAttachmentClick($event, file)">
                <span class="file-icon">📄</span>
                <span class="file-name">{{ file.attachmentName }}</span>
                <span class="file-size">({{ formatFileSize(file.attachmentSize) }})</span>
                <span class="download-icon">⬇️</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getNewsDetail } from '../api'

export default {
  name: 'NewsDetail',
  data() {
    return {
      loading: false,
      error: null,
      newsDetail: {},
      attachments: []
    }
  },
  computed: {
    newsId() {
      return this.$route.params.id
    },
    newsCategory() {
      // 根据 newsType 判断是政策法规还是要闻动态 (2=政策法规, 1=要闻动态)
      const newsType = this.newsDetail.newsType
      if (newsType === 2) {
        return '政策法规'
      } else if (newsType === 1) {
        return '要闻动态'
      }
      return '政策法规'
    }
  },
  mounted() {
    this.fetchNewsDetail()
  },
  methods: {
    // 获取新闻详情
    async fetchNewsDetail() {
      this.loading = true
      this.error = null
      try {
        const res = await getNewsDetail(this.newsId)

        if (res.code === 200 || res.code === 0) {
          // 数据在 res.data 中
          this.newsDetail = res.data || {}
          this.attachments = this.newsDetail.attachments || []
        } else {
          this.error = res.msg || res.message || '获取详情失败'
        }
      } catch (error) {
        console.error('获取新闻详情失败:', error)
        this.error = '网络请求失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },

    goHome() {
      this.$router.push('/')
    },
    goToNewsList() {
      const type = this.newsDetail.newsType === 1 ? 'dynamic' : 'policy'
      this.$router.push({
        path: '/news',
        query: { type }
      })
    },

    resolveAttachmentUrl(path) {
      if (!path) return ''
      if (/^https?:\/\//i.test(path)) return path
      return new URL(path, window.location.origin).href
    },

    sanitizeDownloadName(file) {
      let name = (file.attachmentName || '附件').trim().replace(/[/\\?%*:|"<>]/g, '_')
      if (!name) name = '附件'
      const ext = (file.attachmentSuffix || '').toLowerCase().replace(/^\./, '')
      if (ext && !name.toLowerCase().endsWith('.' + ext)) {
        name = `${name}.${ext}`
      }
      return name
    },

    async onAttachmentClick(e, file) {
      const url = this.resolveAttachmentUrl(file.attachmentPath)
      if (!url) return
      const filename = this.sanitizeDownloadName(file)
      e.preventDefault()
      try {
        const res = await fetch(url, { mode: 'cors', credentials: 'omit' })
        if (!res.ok) throw new Error('fetch failed')
        const blob = await res.blob()
        const objectUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = filename
        a.rel = 'noopener'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(objectUrl)
      } catch {
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    },

    // 格式化文件大小
    formatFileSize(size) {
      if (!size) return '未知大小'
      if (size < 1024) return size + ' B'
      if (size < 1024 * 1024) return (size / 1024).toFixed(2) + ' KB'
      return (size / (1024 * 1024)).toFixed(2) + ' MB'
    }
  }
}
</script>

<style lang="scss" scoped>
.news-detail-page {
  min-height: 100vh;
  background-color: #f5f5f5;
}

// 面包屑导航
.breadcrumb {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 15px 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;

    span {
      font-size: 14px;
      color: #666;

      &.separator {
        margin: 0 8px;
        color: #999;
      }

      &.current {
        color: #333;
      }

      &.link {
        cursor: pointer;
        color: #16a34a;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
}

// 详情内容
.detail-content {
  padding: 30px 0;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .content-card {
    background: white;
    border-radius: 12px;
    padding: 40px 50px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .news-title {
      font-size: 24px;
      font-weight: 600;
      color: #333;
      margin-bottom: 15px;
      line-height: 1.4;
    }

    .news-meta {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 25px;
      padding-bottom: 20px;
      border-bottom: 1px solid #e5e7eb;

      .date-icon {
        font-size: 12px;
        color: #999;
        width: 14px;
        height: 14px;
      }

      .news-date {
        font-size: 14px;
        color: #999;
      }
    }

    // 附件区域
    .attachments-section {
      margin-bottom: 30px;
      padding: 20px;
      background: #f9fafb;
      border-radius: 8px;

      .section-title {
        font-size: 16px;
        font-weight: 600;
        color: #374151;
        margin-bottom: 15px;
      }

      .attachments-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .attachment-item {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 15px;
        background: white;
        border-radius: 6px;
        border: 1px solid #e5e7eb;
        text-decoration: none;
        transition: all 0.3s;

        &:hover {
          border-color: #16a34a;
          box-shadow: 0 2px 8px rgba(22, 163, 74, 0.1);

          .download-icon {
            opacity: 1;
          }
        }

        .file-icon {
          font-size: 20px;
        }

        .file-name {
          flex: 1;
          font-size: 14px;
          color: #374151;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .file-size {
          font-size: 12px;
          color: #9ca3af;
        }

        .download-icon {
          font-size: 16px;
          opacity: 0.5;
          transition: opacity 0.3s;
        }
      }
    }

    .news-body {
      .content-html {
        font-size: 16px;
        color: #333;
        line-height: 1.5;

        ::v-deep {
          p {
            line-height: 1.5;
          }

          img {
            max-width: 100%;
            height: auto;
            border-radius: 8px;
            margin: 15px 0;
          }

          a {
            color: #16a34a;
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }
          }

          ul,
          ol {
            margin: 15px 0;
            padding-left: 25px;
          }

          li {
            margin-bottom: 8px;
          }

          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            margin: 20px 0 15px;
            font-weight: 600;
          }

          blockquote {
            border-left: 4px solid #16a34a;
            padding-left: 15px;
            margin: 15px 0;
            color: #666;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;

            th,
            td {
              border: 1px solid #e5e7eb;
              padding: 10px;
              text-align: left;
            }

            th {
              background: #f9fafb;
              font-weight: 600;
            }
          }
        }
      }
    }
  }
}

// 加载和错误状态
.loading-state,
.error-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  p {
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 15px;
  }

  button {
    padding: 10px 25px;
    background: #16a34a;
    color: white;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font-size: 14px;

    &:hover {
      background: #15803d;
    }
  }
}

@media (max-width: 768px) {
  .detail-content {
    .content-card {
      padding: 25px;

      .news-title {
        font-size: 20px;
      }

      .attachments-section {
        padding: 15px;

        .attachment-item {
          flex-wrap: wrap;

          .file-name {
            width: 100%;
            order: 3;
          }
        }
      }
    }
  }
}
</style>